<template>
	<div class="navigation-language-switcher">
		<NuxtLink
			v-for="availableLocale in availableLocales"
			:key="availableLocale.code"
			prefetch
			@mouseenter="prefetchByUrl(getLocalizedPath(availableLocale.code))"
			:to="getLocalizedPath(availableLocale.code)"
			:class="{ 'active': availableLocale.code === locale }"
		>
			{{ availableLocale.name }}
		</NuxtLink>
	</div>
</template>

<script setup>
import {computed, watch, ref, nextTick} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useSwitchLocalePath } from '#i18n';

const { locale, locales, defaultLocale } = useI18n();
const route = useRoute();
const switchLocalePath = useSwitchLocalePath();

const availableLocales = computed(() => {
	return (locales.value || []).filter(i => i.code !== locale.value);
});

const currentLocalizedPaths = ref({});

const normalizePath = (path) => {
	if (!path) {
		return '/';
	}
	if (path === 'home' || path === '/home') {
		return '/';
	}

	return path.startsWith('/') ? path : `/${path}`;
};

// Watch route changes with immediate cleanup
watch(
() => ({ path: route.fullPath, meta: route.meta }),
	({ meta }) => {
		// Reset to empty object on route change
		currentLocalizedPaths.value = {};

		// Check if meta has localizedPaths
		if (!meta?.localizedPaths) {
			console.log('No localized paths in meta, will use fallback');
			return;
		}

		try {
			let paths = meta.localizedPaths;

			// Handle if it's a ref
			if (paths.value !== undefined) {
				paths = paths.value;
			}

			// Normalize paths
			const normalized = {};
			Object.keys(paths).forEach(key => {
				normalized[key] = normalizePath(paths[key]);
			});

			currentLocalizedPaths.value = normalized;
		} catch (error) {
			console.error('Error processing localized paths:', error);
			currentLocalizedPaths.value = {};
		}
	},
	{ immediate: true }
);

watch(locale, async () => {
	if(process.client) {
		if(typeof window.BrevoConversationsSetup !== 'undefined') {
			window.BrevoConversationsSetup = {
				language: locale.value,
				setChatWidth: 300,
				buttonSize: 40
			};

			BrevoConversations('restart');
		}
	}
});

const getLocalizedPath = (targetLocaleCode) => {
	let path = '';

	const pageLocalizedPaths = currentLocalizedPaths.value;

	if (pageLocalizedPaths && Object.keys(pageLocalizedPaths).length > 0) {
		const specificPath = pageLocalizedPaths[targetLocaleCode];

		if (specificPath) {
			if (targetLocaleCode === defaultLocale && specificPath === '/') {
				path = '/';
			} else if (targetLocaleCode === defaultLocale) {
				path = specificPath;
			} else {
				path = `/${targetLocaleCode}${specificPath}`;
			}
		}
	}

	// 2️⃣ Fallback i18n
	if (!path) {
		path = switchLocalePath(targetLocaleCode)
	}

	return {
		path,
		query: route.query
	};
};

const prefetchByUrl = async (url) => {
	await preloadRouteComponents(url);
};
</script>